// Fetch de los productos (variantes) reales de la empresa del user para
// Interfaz V2. Reemplaza al mock `MOCK_USER_PRODUCTS`.
//
// La data vive en dos tablas:
//   • `variantes` — fila por variante de producto (id, nombre, …).
//   • `productos_imagenes` — filas con imágenes asociadas a una variante.
//
// Las imágenes en `productos_imagenes.url` son nombres de archivo (no URLs).
// El path completo en el bucket `productos` es:
//   `<companyId>/<variantId>/opt/<filename>`   (thumbnail)
//   `<companyId>/<variantId>/full/<filename>`  (full size)
// Si `url` empieza con `http`, es URL completa y se usa tal cual.

import { supabase } from '../../lib/supabase';
import { fetchInChunks } from '../../utils/supabaseChunkedIn';
import type { MockProductV2 } from './mockData';

const PRODUCTOS_BUCKET = 'productos';
const SIGNED_URL_TTL = 3600; // 1h

interface RawImage {
  variante_id: string;
  url: string;
  principal: boolean | null;
}

/**
 * Trae las variantes activas de la empresa + la imagen principal de cada
 * una (signed URL). Mantiene el shape de MockProductV2 para que el resto
 * de la UI (pickers, chips, etc.) no necesite cambios.
 */
export async function fetchUserProducts(
  companyId: string,
  brandId?: string | null,
): Promise<MockProductV2[]> {
  // Joineamos `productos!inner` para leer `model_view` (categoría del
  // producto padre — torso/calzado/cuerpo_completo/etc.). Lo usamos en
  // el product picker para sugerir productos según el template elegido.
  // También traemos `brand_id` para poder filtrar por marca activa (mismo
  // criterio que Estudio Virtual): si `brandId` viene, solo devolvemos los
  // productos de esa marca; si es null/undefined, todos los de la empresa.
  let query = supabase
    .from('variantes')
    .select('id, nombre, created_at, productos!inner(model_view, brand_id)')
    .eq('activo', true)
    .eq('id_company', companyId);
  // Filtramos por marca activa PERO siempre incluimos los productos sin marca
  // (`brand_id IS NULL`): son productos de la empresa que nunca se asignaron a
  // una marca (onboarding viejo, etc.). Sin esto, una empresa con marca default
  // no vería sus productos huérfanos. Ver [[productos-huerfanos-brand-null]].
  if (brandId) {
    query = query.or(`brand_id.eq.${brandId},brand_id.is.null`, {
      referencedTable: 'productos',
    });
  }
  const { data: variants, error: varErr } = await query.order('created_at', {
    ascending: false,
  });
  if (varErr || !variants || variants.length === 0) return [];

  const variantIds = (variants as any[]).map((v) => v.id as string);
  // Fetch en lotes para no exceder el largo de URL del gateway REST con
  // catálogos grandes (devolvía HTTP 400). Ver utils/supabaseChunkedIn.
  const imagesRaw = await fetchInChunks<RawImage>(variantIds, (chunk) =>
    supabase
      .from('productos_imagenes')
      .select('variante_id, url, principal')
      .eq('id_company', companyId)
      .in('variante_id', chunk),
  );

  // Agrupar imágenes por variante. Preferimos la principal; sino, la
  // primera que aparece (ya viene ordenada por la query, aunque no
  // ordenamos explícitamente — para mejorar, se podría agregar order
  // by orden ASC).
  const byVariant = new Map<string, RawImage>();
  for (const img of ((imagesRaw || []) as any[]) as RawImage[]) {
    const existing = byVariant.get(img.variante_id);
    if (!existing) {
      byVariant.set(img.variante_id, img);
    } else if (img.principal && !existing.principal) {
      byVariant.set(img.variante_id, img);
    }
  }

  // Firmar URLs en paralelo para todas las variantes con imagen.
  const products: MockProductV2[] = await Promise.all(
    (variants as any[]).map(async (v): Promise<MockProductV2> => {
      const img = byVariant.get(v.id);
      let imageUrl = '';
      if (img?.url) {
        if (img.url.startsWith('http')) {
          imageUrl = img.url;
        } else {
          const optPath = `${companyId}/${v.id}/opt/${img.url}`;
          const { data: signed } = await supabase.storage
            .from(PRODUCTOS_BUCKET)
            .createSignedUrl(optPath, SIGNED_URL_TTL);
          if (signed?.signedUrl) {
            imageUrl = signed.signedUrl;
          } else {
            // Fallback al full si el opt no existe.
            const fullPath = `${companyId}/${v.id}/full/${img.url}`;
            const { data: signedFull } = await supabase.storage
              .from(PRODUCTOS_BUCKET)
              .createSignedUrl(fullPath, SIGNED_URL_TTL);
            imageUrl = signedFull?.signedUrl || '';
          }
        }
      }
      // `productos` viene del join — puede ser objeto o array según el
      // cliente. Normalizamos a string, recortamos whitespace y bajamos
      // a lowercase para que el match contra `tipos_producto_sugeridos`
      // sea robusto a typos de carga (algunos rows tienen "torso\n").
      const productosRel = (v as any).productos;
      const rawModelView = Array.isArray(productosRel)
        ? productosRel[0]?.model_view
        : productosRel?.model_view;
      const modelView =
        typeof rawModelView === 'string'
          ? rawModelView.trim().toLowerCase() || null
          : null;
      return {
        id: v.id as string,
        name: v.nombre as string,
        imageUrl,
        modelView,
        createdAt: v.created_at as string,
      };
    }),
  );
  return products;
}

/**
 * URL firmada de la imagen PRINCIPAL de una variante en tamaño FULL — para
 * usarla como REFERENCIA de generación (ADR-586). El `imageUrl` del catálogo
 * es el thumbnail `opt` de 300px: alcanza para pickers pero es pobre como
 * referencia de fidelidad de producto. Fallback al `opt` si el full no está.
 * Devuelve null si la variante no tiene imagen.
 */
export async function getProductFullImageUrl(
  companyId: string,
  variantId: string,
): Promise<string | null> {
  const { data } = await supabase
    .from('productos_imagenes')
    .select('url, principal, orden')
    .eq('variante_id', variantId)
    .eq('id_company', companyId)
    .order('principal', { ascending: false })
    .order('orden', { ascending: true })
    .limit(1)
    .maybeSingle();
  const fileName = (data as any)?.url as string | undefined;
  if (!fileName) return null;
  if (fileName.startsWith('http')) return fileName;
  const { data: signedFull } = await supabase.storage
    .from(PRODUCTOS_BUCKET)
    .createSignedUrl(`${companyId}/${variantId}/full/${fileName}`, SIGNED_URL_TTL);
  if (signedFull?.signedUrl) return signedFull.signedUrl;
  const { data: signedOpt } = await supabase.storage
    .from(PRODUCTOS_BUCKET)
    .createSignedUrl(`${companyId}/${variantId}/opt/${fileName}`, SIGNED_URL_TTL);
  return signedOpt?.signedUrl || null;
}

/**
 * Igual que `fetchUserProducts` pero filtrando por la marca (`brand_id` del
 * producto) — para el editor de video, que muestra solo los productos de la
 * marca del proyecto, no de toda la cuenta. Reusa el mismo filtro inner-join
 * (`productos!inner(brand_id)`) que ya usa Estudio Virtual en la web.
 *
 * Incluye SIEMPRE los productos sin marca (`brand_id IS NULL`) además de los de
 * la marca pedida: una empresa nunca debe quedar sin ver sus productos
 * huérfanos. Ver [[productos-huerfanos-brand-null]].
 */
export async function fetchUserProductsByBrand(
  companyId: string,
  brandId: string,
): Promise<MockProductV2[]> {
  const { data: variants, error: varErr } = await supabase
    .from('variantes')
    .select('id, nombre, created_at, productos!inner(brand_id)')
    .eq('activo', true)
    .eq('id_company', companyId)
    .or(`brand_id.eq.${brandId},brand_id.is.null`, {
      referencedTable: 'productos',
    })
    .order('created_at', { ascending: false });
  if (varErr)
    console.error('[fetchUserProductsByBrand] variantes query error:', varErr);
  console.log('[fetchUserProductsByBrand]', {
    companyId,
    brandId,
    variantes: variants?.length ?? 0,
  });
  if (varErr || !variants || variants.length === 0) return [];

  const variantIds = (variants as any[]).map((v) => v.id as string);
  // Fetch en lotes para no exceder el largo de URL del gateway REST con
  // catálogos grandes (devolvía HTTP 400). Ver utils/supabaseChunkedIn.
  const imagesRaw = await fetchInChunks<RawImage>(variantIds, (chunk) =>
    supabase
      .from('productos_imagenes')
      .select('variante_id, url, principal')
      .eq('id_company', companyId)
      .in('variante_id', chunk),
  );

  const byVariant = new Map<string, RawImage>();
  for (const img of ((imagesRaw || []) as any[]) as RawImage[]) {
    const existing = byVariant.get(img.variante_id);
    if (!existing) {
      byVariant.set(img.variante_id, img);
    } else if (img.principal && !existing.principal) {
      byVariant.set(img.variante_id, img);
    }
  }

  return Promise.all(
    (variants as any[]).map(async (v): Promise<MockProductV2> => {
      const img = byVariant.get(v.id);
      let imageUrl = '';
      if (img?.url) {
        if (img.url.startsWith('http')) {
          imageUrl = img.url;
        } else {
          const optPath = `${companyId}/${v.id}/opt/${img.url}`;
          const { data: signed } = await supabase.storage
            .from(PRODUCTOS_BUCKET)
            .createSignedUrl(optPath, SIGNED_URL_TTL);
          if (signed?.signedUrl) {
            imageUrl = signed.signedUrl;
          } else {
            const fullPath = `${companyId}/${v.id}/full/${img.url}`;
            const { data: signedFull } = await supabase.storage
              .from(PRODUCTOS_BUCKET)
              .createSignedUrl(fullPath, SIGNED_URL_TTL);
            imageUrl = signedFull?.signedUrl || '';
          }
        }
      }
      return { id: v.id as string, name: v.nombre as string, imageUrl };
    }),
  );
}

const FONDOS_BUCKET = 'fondos_personalizados';

/**
 * Escenas para el editor de video. Combina:
 *  • las escenas/fondos guardados por el cliente (`fondos_personalizados`,
 *    bucket homónimo, por empresa), y
 *  • la biblioteca default de ATLAS (`fondos`), filtrada por el rubro de la
 *    empresa (mismo criterio que Estudio Virtual: `rubros` incluye el rubro o
 *    es `['all']`). `fondos.link_img` ya es una URL directa.
 * Devuelve el shape de MockProductV2 para reusar la grilla del editor.
 */
export async function fetchSceneImages(
  companyId: string,
): Promise<MockProductV2[]> {
  const out: MockProductV2[] = [];

  // 1. Escenas guardadas por el cliente.
  const { data: custom } = await supabase
    .from('fondos_personalizados')
    .select('id, nombre, nombre_archivo, created_at')
    .eq('id_company', companyId)
    .order('created_at', { ascending: false });
  for (const s of ((custom || []) as any[])) {
    let imageUrl = '';
    if (s.nombre_archivo) {
      const { data: signed } = await supabase.storage
        .from(FONDOS_BUCKET)
        .createSignedUrl(`${companyId}/full/${s.nombre_archivo}`, SIGNED_URL_TTL);
      imageUrl = signed?.signedUrl || '';
    }
    if (imageUrl)
      out.push({
        id: `scene-custom-${s.id}`,
        name: (s.nombre as string) || 'Escena',
        imageUrl,
      });
  }

  // 2. Biblioteca default de ATLAS, filtrada por rubro de la empresa.
  let rubro: string | null = null;
  try {
    const { data: comp } = await supabase
      .from('company_profiles')
      .select('rubro')
      .eq('id_company', companyId)
      .single();
    rubro = (comp as any)?.rubro ?? null;
  } catch {
    /* sin rubro → solo se muestran los fondos marcados 'all' */
  }
  const { data: defaults } = await supabase
    .from('fondos')
    .select('id, link_img, nombre, rubros');
  for (const f of ((defaults || []) as any[])) {
    const arr = (f?.rubros as string[] | undefined) || [];
    const ok =
      (arr.length === 1 && arr[0] === 'all') || (rubro ? arr.includes(rubro) : false);
    if (!ok || !f.link_img) continue;
    out.push({
      id: `scene-fondo-${f.id}`,
      name: (f.nombre as string) || 'Escena',
      imageUrl: f.link_img as string,
    });
  }

  return out;
}

const GENERADAS_BUCKET = 'generadas';

/**
 * Historial de imágenes generadas por el cliente con ATLAS (Estudio Virtual),
 * filtrado por la marca del proyecto — igual que productos: el vínculo con la
 * marca es vía `variante_id` → producto → `brand_id` (las generaciones sin
 * variante simplemente no aparecen). Solo imágenes (filtra por extensión),
 * activas, más recientes primero. Devuelve el shape de MockProductV2.
 */
export async function fetchHistorialImages(
  companyId: string,
  brandId: string,
): Promise<MockProductV2[]> {
  const { data, error } = await supabase
    .from('imagenes_generadas')
    .select('id, file_name, created_at, variantes!inner(productos!inner(brand_id))')
    .eq('id_company', companyId)
    .eq('active', true)
    .eq('variantes.productos.brand_id', brandId)
    .order('created_at', { ascending: false })
    .limit(80);
  if (error)
    console.error('[fetchHistorialImages] query error:', error);
  if (error || !data) return [];

  const imgs = (data as any[]).filter(
    (r) => r.file_name && /\.(png|jpe?g|webp)$/i.test(r.file_name as string),
  );
  return Promise.all(
    imgs.map(async (r): Promise<MockProductV2> => {
      // Thumbnail opt/ (300px) para que la grilla cargue rápido; fallback a
      // full/ si el opt no existe.
      let imageUrl = '';
      const optPath = `${companyId}/opt/${r.file_name}`;
      const { data: signed } = await supabase.storage
        .from(GENERADAS_BUCKET)
        .createSignedUrl(optPath, SIGNED_URL_TTL);
      if (signed?.signedUrl) {
        imageUrl = signed.signedUrl;
      } else {
        const fullPath = `${companyId}/full/${r.file_name}`;
        const { data: signedFull } = await supabase.storage
          .from(GENERADAS_BUCKET)
          .createSignedUrl(fullPath, SIGNED_URL_TTL);
        imageUrl = signedFull?.signedUrl || '';
      }
      return {
        id: `hist-${r.id}`,
        name: 'Imagen generada',
        imageUrl,
      };
    }),
  );
}
